import { useModal } from "@ebay/nice-modal-react";
import ErrorOutlineSharpIcon from "@mui/icons-material/ErrorOutlineSharp";
import {
  Box,
  Card,
  CardContent,
  List,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import useQueryStringState from "components/hooks/useQueryStringState";
import Pagination from "components/ui/atoms/Pagination";
import FilterAndSort from "components/ui/molecules/FilterAndSort";
import Layout from "components/ui/molecules/Layout";
import { useAllSourcing } from "models/sourcings/apiSourcing";
import CommitteeFilterAndSort from "models/sourcings/CommitteeFilterAndSort";
import CommitteeList from "models/sourcings/CommitteeList";
import SourcingFilterForm from "models/sourcings/SourcingFilterForm";
import SourcingHeader from "models/sourcings/SourcingHeader";
import SourcingListItem from "models/sourcings/SourcingListItem";
import SourcingSortForm from "models/sourcings/SourcingSortForm";
import { useMemo } from "react";

export const DISPLAY_MODE_COMMITTEE = "COMMITTEE";

const Sourcing = ({}) => {
  const theme = useTheme();

  const [state, setState] = useQueryStringState();
  const sourcingModal = useModal("SourcingDrawer");

  const {
    data: sourcings,
    isLoading: sourcingsIsLoading,
    isError: sourcingsIsError,
    isSuccess: sourcingsIsSuccess,
    refetch,
  } = useAllSourcing({
    ...state.filter,
    ...state.sort,
    offset: state.offset,
    search: state.search,
    display_mode: state.displayMode,
  });

  const setDisplayMode = (displayMode) => {
    setState((prevState) => ({
      ...prevState,
      displayMode: displayMode,
    }));
  };

  const sourcingIds = useMemo(() => {
    if (state.displayMode !== DISPLAY_MODE_COMMITTEE)
      return sourcings?.results?.length > 1
        ? sourcings.results.map((sourcing) => sourcing.id)
        : null;
    else {
      let ids = [];
      if (sourcings?.results) {
        Object.entries(sourcings.results).map(([key, list]) => {
          list.map((sourcing) => ids.push(sourcing.id));
        });
        return ids;
      }
    }
  }, [state.displayMode, sourcings]);

  const sourcingsSkeleton = (
    <Stack spacing={2}>
      <Skeleton variant="rectangular" height={150} />
      <Skeleton variant="rectangular" height={150} />
      <Skeleton variant="rectangular" height={150} />
    </Stack>
  );

  if (sourcingsIsError) return <>Error</>;

  return (
    <Layout container hideBreadcrumbs>
      <Layout title variant="component">
        <SourcingHeader
          displayMode={state.displayMode}
          setDisplayMode={setDisplayMode}
        />
      </Layout>
      <Layout content>
        <FilterAndSort
          FilterForm={SourcingFilterForm}
          SortForm={SourcingSortForm}
          state={state}
          setState={setState}
          count={sourcings?.count}
        />
        {state.displayMode == DISPLAY_MODE_COMMITTEE && (
          <CommitteeFilterAndSort
            state={state.filter}
            setState={(new_state) => {
              setState({
                ...state,
                filter: new_state,
                offset: 0,
              });
            }}
            onClose={() =>
              setState({
                ...state,
                displayMode: undefined,
                offset: 0,
              })
            }
            refetchList={refetch}
          />
        )}
      </Layout>
      <Layout content>
        {sourcingsIsLoading ? (
          sourcingsSkeleton
        ) : state.displayMode == DISPLAY_MODE_COMMITTEE ? (
          sourcings.count > 500 ? (
            <Card
              sx={{
                backgroundColor: `${theme.palette.warning.light}22`,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center" }}>
                <ErrorOutlineSharpIcon sx={{ mr: 2 }} />
                <Typography variant="h6" textAlign="center">
                  Please select more filters{" "}
                  <Typography variant="caption">{`(max results < 500)`}</Typography>
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <CommitteeList
              committeeSourcingList={sourcings.results}
              onClick={(sourcingId) =>
                sourcingModal
                  .show({
                    formType: "update",
                    id: sourcingId,
                    values: sourcingIds,
                  })
                  .then(refetch)
              }
            />
          )
        ) : (
          <>
            <List variant="boxed">
              {sourcings.results.map((sourcing, index) => (
                <SourcingListItem
                  key={index}
                  sourcing={sourcing}
                  onClick={(sourcingId) =>
                    sourcingModal
                      .show({
                        formType: "update",
                        id: sourcingId,
                        values: sourcingIds,
                      })
                      .then(refetch)
                  }
                />
              ))}
            </List>
            <Box alignItems="center" justifyContent="center" mt={4}>
              <Pagination
                offset={state.offset || 0}
                setOffset={(offset) => {
                  setState({
                    ...state,
                    offset: offset,
                  });
                }}
                count={sourcings.count}
              />
            </Box>
          </>
        )}
      </Layout>
    </Layout>
  );
};

export default Sourcing;
